import React from 'react';
import { format } from 'date-fns';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const RegistrationModal = ({ event, isRegistered, onClose }) => {
  const handleAddToCalendar = () => {
    // Create calendar event URL
    const startDate = new Date(event?.start_date);
    const endDate = event?.end_date ? new Date(event?.end_date) : new Date(startDate.getTime() + 2 * 60 * 60 * 1000);
    
    const formatDate = (date) => {
      return date?.toISOString()?.replace(/[-:]/g, '')?.split('.')?.[0] + 'Z';
    };

    const calendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(event?.title)}&dates=${formatDate(startDate)}/${formatDate(endDate)}&details=${encodeURIComponent(event?.description || '')}&location=${encodeURIComponent(event?.location || event?.virtual_link || '')}`;
    
    window.open(calendarUrl, '_blank');
  };

  const handleShare = async () => {
    const shareData = {
      title: event?.title,
      text: `Join me at ${event?.title} on ${format(new Date(event?.start_date), 'MMMM dd, yyyy')}`,
      url: window.location?.href
    };

    try {
      if (navigator.share && navigator.canShare?.(shareData)) {
        await navigator.share(shareData);
      } else {
        // Fallback to clipboard
        await navigator.clipboard?.writeText(`${shareData?.text} ${shareData?.url}`);
        // You could show a toast notification here
      }
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-card rounded-lg max-w-md w-full p-6 space-y-4">
        <div className="text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="CheckCircle" size={32} className="text-green-600" />
          </div>
          
          <h3 className="text-lg font-semibold mb-2">
            {isRegistered ? "Registration Confirmed!" : "Event Information"}
          </h3>
          
          <p className="text-muted-foreground text-sm mb-4">
            {isRegistered 
              ? "You're all set for this event. We'll send you a reminder before it starts."
              : "Here's what you need to know about this event."
            }
          </p>
        </div>

        <div className="bg-muted/30 rounded-lg p-4 space-y-3">
          <div className="flex items-start space-x-3">
            <Icon name="Calendar" size={16} className="text-muted-foreground mt-0.5" />
            <div>
              <p className="text-sm font-medium">{event?.title}</p>
              <p className="text-xs text-muted-foreground">
                {format(new Date(event?.start_date), 'EEEE, MMMM dd, yyyy')} at {format(new Date(event?.start_date), 'h:mm a')}
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3">
            <Icon name={event?.is_virtual ? "Monitor" : "MapPin"} size={16} className="text-muted-foreground mt-0.5" />
            <div>
              <p className="text-sm">{event?.is_virtual ? 'Virtual Event' : event?.location}</p>
              {event?.is_virtual && event?.virtual_link && (
                <p className="text-xs text-muted-foreground">Link will be shared via email</p>
              )}
            </div>
          </div>

          {event?.organizer && (
            <div className="flex items-start space-x-3">
              <Icon name="User" size={16} className="text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm">Organized by {event?.organizer?.full_name}</p>
                {event?.organizer?.organization && (
                  <p className="text-xs text-muted-foreground">{event?.organizer?.organization}</p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleAddToCalendar}
            className="flex-1"
          >
            <Icon name="Calendar" size={14} className="mr-2" />
            Add to Calendar
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleShare}
            className="flex-1"
          >
            <Icon name="Share2" size={14} className="mr-2" />
            Share Event
          </Button>
        </div>

        {isRegistered && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
            <div className="flex items-start space-x-2">
              <Icon name="Mail" size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-green-800">
                <p className="font-medium">Confirmation Email Sent</p>
                <p>Check your inbox for event details and any special instructions.</p>
              </div>
            </div>
          </div>
        )}

        <Button onClick={onClose} className="w-full">
          {isRegistered ? "Got it, thanks!" : "Close"}
        </Button>
      </div>
    </div>
  );
};

export default RegistrationModal;